import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import SEOHead from '@/components/common/SEOHead';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';

type State = 'idle' | 'loading' | 'success' | 'already' | 'error';

const UnsubscribeNotifications: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState<State>('idle');
  const [message, setMessage] = useState<string>('');
  const triggered = useRef(false);

  const submit = async () => {
    if (!token) {
      setState('error');
      setMessage('This unsubscribe link is missing its token. Please use the link from the email exactly as received.');
      return;
    }
    setState('loading');
    const { data, error } = await supabase.functions.invoke('unsubscribe-notification', {
      body: { token },
    });
    if (error || !data?.ok) {
      setState('error');
      setMessage('This link is invalid or expired.');
      return;
    }
    if (data.alreadyUsed) {
      setState('already');
      setMessage("You're already unsubscribed from AI-review-ready emails.");
      return;
    }
    setState('success');
    setMessage("You've been unsubscribed from AI-review-ready emails. You can re-enable this anytime in Settings.");
  };

  // Auto-confirm on mount when a token is present so List-Unsubscribe one-click
  // and email link clicks complete without an extra confirmation step.
  useEffect(() => {
    if (triggered.current) return;
    triggered.current = true;
    if (token) void submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="flex flex-col min-h-screen">
      <SEOHead
        title="Unsubscribe from notifications — PaperVerse"
        description="Opt out of AI-review-ready email notifications."
        noindex
      />
      <Navbar />
      <main className="flex-1 container max-w-2xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-semibold tracking-tight mb-2">
          Unsubscribe from AI-review-ready emails
        </h1>
        <p className="text-muted-foreground mb-6">
          Confirm below to stop receiving emails when an AI review finishes for your papers.
        </p>
        <Card>
          <CardHeader>
            <CardTitle>AI-review-ready notifications</CardTitle>
            <CardDescription>
              You can re-enable this anytime in Settings → Notifications.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {state === 'idle' && (
              <Button onClick={submit} disabled={!token}>
                Confirm unsubscribe
              </Button>
            )}
            {state === 'loading' && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Processing your request…
              </div>
            )}
            {state === 'success' && (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>{message}</AlertDescription>
              </Alert>
            )}
            {state === 'already' && (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>{message}</AlertDescription>
              </Alert>
            )}
            {state === 'error' && (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertDescription>{message}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default UnsubscribeNotifications;
